import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

/**
 * Where a restaurant user lets an AI agent use OrderHub's MCP server as them
 * (`packages/backend/convex/mcp/README.md`). The agent opens it as `?request=<id>`; the root route
 * signs the user in first, on this same URL. For venues with the `mcp-server` feature flag on.
 */
export const Route = createFileRoute('/connect-agent')({
  validateSearch: z.object({ request: z.string().optional() }),
  component: ConnectAgentPage,
});

function ConnectAgentPage() {
  const { request: requestId } = Route.useSearch();
  const { convexUser } = useRouteContext({ from: '__root__' });

  // Clickjacking: a page framing this one could trick a click on "Allow". vercel.json forbids
  // framing it too; this covers hosts that don't send that header (local dev, pods).
  if (window.top !== window.self) return <Message text="Open this page in its own tab." />;
  if (convexUser.userType !== 'restaurantUser') {
    return (
      <Message text="Only venue accounts can connect an AI agent. Sign out, then sign in with your venue's account." />
    );
  }
  if (!requestId) {
    return (
      <Message text="Start from your AI agent. It opens this page when it's ready to connect." />
    );
  }
  return <ConsentCard requestId={requestId} />;
}

/**
 * What this page reads of `api.mcp.getAuthRequest`. The component's `McpServer.api()` builds it
 * without a `returns` validator, so static codegen types it `any`. The device flow is off
 * (`deviceFlow: false` on both MCP servers), so every request redirects back to its agent.
 */
type AuthRequest = {
  clientName?: string;
  redirectUri: string;
  scopes: { name: string; description: string }[];
  status: 'pending' | 'approved' | 'denied';
} | null;

function ConsentCard({ requestId }: { requestId: string }) {
  const convex = useConvex();
  // The signed-in restaurant user's venue: the one the agent will act on (`mcp/domain.ts`).
  const featureFlagsQuery = useQuery(api.featureFlags.listByRestaurantUser, {});
  const { data, error, isPending } = useQuery(api.mcp.getAuthRequest, { requestId });
  const authRequest: AuthRequest | undefined = data;
  const [decision, setDecision] = useState<'allowing' | 'denying' | null>(null);

  if (isPending || featureFlagsQuery.isPending) return <LoadingIcon />;
  if (error) return <ErrorAlert title="Error loading the request" message={error.message} />;
  if (featureFlagsQuery.isError) {
    return (
      <ErrorAlert title="Error loading your venue" message={featureFlagsQuery.error.message} />
    );
  }
  if (!featureFlagsQuery.data.some((featureFlag) => featureFlag.key === 'mcp-server')) {
    return <Message text="AI agents aren't turned on for your venue." />;
  }
  if (!authRequest) {
    return <Message text="This link has expired. Start connecting again from your AI agent." />;
  }
  if (authRequest.status !== 'pending') {
    return <Message text="All done. You can go back to your AI agent." />;
  }

  const decide = async (approve: boolean) => {
    setDecision(approve ? 'allowing' : 'denying');
    try {
      // Typed `any` by static codegen, as `AuthRequest`. Never null: that's for the device flow.
      const { redirectUrl }: { redirectUrl: string } = await convex.action(api.mcp.authorize, {
        requestId,
        approve,
      });
      window.location.assign(redirectUrl); // Back to the agent
    } catch (decideError) {
      setDecision(null);
      toast.error("Couldn't save your answer", { description: getUserErrorMessage(decideError) });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Let {authRequest.clientName ?? 'this AI agent'} use OrderHub?</CardTitle>
          <CardDescription>
            It works with your venue as you. The agent names itself, so only allow it if you just
            started connecting it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <p className="font-medium">It'll be able to:</p>
            <ul className="list-disc pl-5">
              {authRequest.scopes.map((scope) => (
                <li key={scope.name}>{scope.description}</li>
              ))}
            </ul>
          </div>
          <p className="text-muted-foreground">
            Then you'll go back to {new URL(authRequest.redirectUri).host}.
          </p>
          <p className="text-muted-foreground">
            You can disconnect it any time in Settings, AI agents.
          </p>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <ActionButton
            disabled={decision !== null}
            loading={decision === 'denying'}
            onClick={() => decide(false)}
            variant="outline"
          >
            Don't allow
          </ActionButton>
          <ActionButton
            disabled={decision !== null}
            loading={decision === 'allowing'}
            onClick={() => decide(true)}
          >
            Allow
          </ActionButton>
        </CardFooter>
      </Card>
    </div>
  );
}

function Message({ text }: { text: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4 text-center">
      <p className="max-w-md">{text}</p>
    </div>
  );
}
